import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'src/shared/components/modal/Modal';
import { Loader } from 'src/shared/components/loader/Loader';
import DataTable from 'src/shared/components/data-table/DataTable';
import { DataTableColumns, DataTableState, RowsPerPage, SortingDirection } from 'src/shared/components/data-table/dataTableTypes';
import SearchInput from 'src/shared/components/search-input/SearchInput';
import { useDebouncedListSearch } from 'src/shared/hooks/useDebouncedListSearch';
import { formatSquareMetresToKm2 } from 'src/shared/helpers/areaHelpers';
import { describeOverlap } from '../../state/api/overlapDisplay';
import { useGetSiteProtectedAreasQuery } from '../../state/api/siteNatureRiskEngineSlice';

type ProtectedAreasModalProps = {
    organisationBusinessCentralId: string;
    siteId: string;
    /** Selected time period — the list reflects the overlaps recorded then. */
    at?: string;
    onClose: () => void;
};

// A site can overlap hundreds of areas, so the engine pages and searches; the
// modal only ever holds the page on screen.
const ROWS_PER_PAGE = 10;

// Overlap area is populated only when the area intersects the footprint; an
// adjacent area covers none of the site.
const tableColumns: DataTableColumns = [
    { columnId: 'areaName', title: 'Area Name', width: '52%', isSortable: true },
    { columnId: 'relationship', title: 'Relationship', width: '24%' },
    { columnId: 'overlap', title: 'Overlap', width: '24%' },
];

// The engine orders by distance; the header flips that order.
const SORT_COLUMN = 'areaName';

const ProtectedAreasModal = ({ organisationBusinessCentralId, siteId, at, onClose }: ProtectedAreasModalProps) => {
    const [page, setPage] = useState(1);
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
    const { inputValue, activeSearch, onInputChange } = useDebouncedListSearch();

    const { data, isLoading, isFetching, isError } = useGetSiteProtectedAreasQuery({
        organisationBusinessCentralId,
        siteId,
        at,
        page,
        pageSize: ROWS_PER_PAGE,
        search: activeSearch,
        sortDirection,
    });

    // A new term or order re-arranges the whole list, so its pages start again.
    useEffect(() => {
        setPage(1);
    }, [activeSearch, sortDirection]);

    const handleStateChange = (newState: DataTableState) => {
        const sorted = newState.sortedColumn;
        if (sorted?.columnId !== SORT_COLUMN || sorted.sortedDirection === SortingDirection.NONE) {
            return;
        }
        setSortDirection(sorted.sortedDirection === SortingDirection.DESC ? 'DESC' : 'ASC');
    };

    const areas = data?.site?.protectedAreas.items ?? [];
    const total = data?.site?.protectedAreas.pagination.totalItems ?? 0;

    const tableData: DataTableState['data'] = useMemo(
        () => areas.map(area => [area.name, describeOverlap(area.overlapType), formatSquareMetresToKm2(area.overlapAreaM2)]),
        [areas]
    );

    return (
        // Fixed height sized to a full page of rows, so searching down to a couple of
        // matches doesn't resize the modal under the cursor.
        <Modal dismissOnClickOutside={true} classNames={{ body: 'w-[800px] h-[672px] p-5' }} onClose={onClose}>
            {isLoading ? (
                <div className='flex items-center justify-center h-full'>
                    <Loader />
                </div>
            ) : isError ? (
                // The table's own empty state would read "no matches for your
                // search", which is a different and wrong explanation.
                <div data-testid='protected-areas-modal-error' className='flex items-center justify-center h-full text-base text-grey-80'>
                    Protected areas could not be loaded. Try again shortly.
                </div>
            ) : (
                <div className='flex flex-col gap-3 h-full' data-testid='protected-areas-modal'>
                    <div className='flex flex-col gap-1'>
                        <div className='text-3xl text-primary'>Protected areas ({total})</div>
                        <div className='text-base text-grey-80'>Protected areas overlapping or adjacent to this site</div>
                    </div>
                    <div className='border-t border-grey-20 h-[1px]'>&nbsp;</div>

                    <DataTable
                        state={{
                            data: tableData,
                            rowsPerPage: ROWS_PER_PAGE as RowsPerPage,
                            currentPageNumber: page,
                            sortedColumn: {
                                columnId: SORT_COLUMN,
                                sortedDirection: sortDirection === 'DESC' ? SortingDirection.DESC : SortingDirection.ASC,
                            },
                        }}
                        columns={tableColumns}
                        totalRowsForPagination={total}
                        onPageChange={setPage}
                        onStateChange={handleStateChange}
                        isServerSideSort={true}
                        // The list is always ordered by distance, so there is no
                        // unsorted state to clear back to — the header just flips
                        // nearest-first / furthest-first.
                        allowSortClear={false}
                        isLoadingOverlay={isFetching}
                        emptyStateMessage='No protected areas match your search.'
                        paginationPosition='top'
                        compactPagination
                        topPaginationAside={
                            <SearchInput placeholder='Search protected areas' value={inputValue} onChange={onInputChange} width='340px' />
                        }
                    />
                </div>
            )}
        </Modal>
    );
};

export default ProtectedAreasModal;
